// «Volver a conectar» una cuenta de Claude (22-10-2026, docs/cuentas-claude/reconexion.md). La ventana enseña un
// navegador que funciona en la máquina de esa cuenta, con su misma conexión: Pau entra con el correo de la cuenta y
// autoriza; la oficina copia el código y comprueba la llave. Sin VPN, sin Firefox, sin Cursor.
import * as Dialogo from '@radix-ui/react-dialog';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { t } from '../../../i18n';
import { api, ApiHttpError } from '../../../lib/api';
import { getSocket } from '../../../lib/socket';
import { cn } from '../../../lib/utils';

type Fase = 'pidiendo' | 'comprobando_ip' | 'abriendo' | 'esperando_entrada' | 'comprobando' | 'conectada' | 'fallo' | 'cancelada';
interface Evento {
  sesionId: string;
  fase: Exclude<Fase, 'pidiendo'>;
  motivo?: string;
  cuadro?: { jpeg: string; ancho: number; alto: number };
}

const TECLAS = new Set(['Enter', 'Backspace', 'Tab', 'Escape', 'ArrowDown', 'ArrowUp']);
const PASO: Record<Fase, number> = { pidiendo: 0, comprobando_ip: 0, abriendo: 0, esperando_entrada: 1, comprobando: 3, conectada: 4, fallo: 0, cancelada: 0 };

function orden(sesionId: string, cuerpo: Record<string, unknown>): void {
  void api(`/api/cuentas-claude/reconexion/${encodeURIComponent(sesionId)}/orden`, { method: 'POST', body: cuerpo }).catch(() => undefined);
}

export function VentanaReconexion({ runnerId, nombre, alCerrar }: { runnerId: string; nombre: string; alCerrar: () => void }) {
  const [sesionId, setSesionId] = useState<string | null>(null);
  const [fase, setFase] = useState<Fase>('pidiendo');
  const [motivo, setMotivo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cuadro, setCuadro] = useState<string | null>(null);
  const [enlace, setEnlace] = useState('');
  const [texto, setTexto] = useState('');
  const pantalla = useRef<HTMLDivElement>(null);
  const sesionRef = useRef<string | null>(null);
  // Lo que llega de la máquina antes de saber el número de sesión (si la máquina se niega al momento, su respuesta
  // puede adelantarse a la del servidor): se guarda y se aplica en cuanto se sabe.
  const pendientes = useRef<Evento[]>([]);
  const ultimaNoticia = useRef(Date.now());
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    let vivo = true;
    setFase('pidiendo');
    setMotivo(null);
    setCuadro(null);
    api<{ sesionId: string }>(`/api/cuentas-claude/${encodeURIComponent(runnerId)}/reconexion`, { method: 'POST' })
      .then((r) => {
        if (!vivo) return;
        sesionRef.current = r.sesionId;
        setSesionId(r.sesionId);
        setFase('comprobando_ip');
        ultimaNoticia.current = Date.now();
        const mias = pendientes.current.filter((e) => e.sesionId === r.sesionId);
        pendientes.current = [];
        for (const e of mias) aplicar.current(e);
      })
      .catch((err: unknown) => {
        if (!vivo) return;
        setFase('fallo');
        setMotivo(err instanceof ApiHttpError ? err.message : t('cuentasClaude.reconexion.noSePudo'));
      });
    return () => {
      vivo = false;
    };
  }, [runnerId, intento]);

  const aplicar = useRef((e: Evento) => {
    ultimaNoticia.current = Date.now();
    if (e.cuadro) {
      setCuadro(`data:image/jpeg;base64,${e.cuadro.jpeg}`);
      return;
    }
    if (e.fase === 'esperando_entrada' && e.motivo) {
      setAviso(e.motivo);
      return;
    }
    setFase(e.fase);
    if (e.motivo) setMotivo(e.motivo);
  });

  useEffect(() => {
    const socket = getSocket();
    const alEvento = (e: Evento) => {
      if (sesionRef.current === null) {
        if (pendientes.current.length < 50) pendientes.current.push(e);
        return;
      }
      if (e.sesionId === sesionRef.current) aplicar.current(e);
    };
    socket.on('cuentas:reconexion', alEvento);
    return () => {
      socket.off('cuentas:reconexion', alEvento);
    };
  }, []);

  // Si la máquina deja de dar noticias mientras se prepara, no se queda la rueda para siempre.
  useEffect(() => {
    if (!['comprobando_ip', 'abriendo'].includes(fase)) return;
    const id = window.setInterval(() => {
      if (Date.now() - ultimaNoticia.current > 60_000) {
        setFase('fallo');
        setMotivo(t('cuentasClaude.reconexion.sinRespuesta'));
      }
    }, 5_000);
    return () => window.clearInterval(id);
  }, [fase]);

  const cerrar = useCallback(() => {
    if (sesionRef.current && !['conectada', 'fallo', 'cancelada'].includes(fase)) orden(sesionRef.current, { orden: 'cancelar' });
    alCerrar();
  }, [fase, alCerrar]);

  const activa = sesionId !== null && (fase === 'esperando_entrada' || fase === 'comprobando');

  function alClic(e: React.MouseEvent<HTMLImageElement>) {
    if (!sesionId || !activa) return;
    const r = e.currentTarget.getBoundingClientRect();
    orden(sesionId, { orden: 'clic', x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
    pantalla.current?.focus();
  }

  function alTecla(e: KeyboardEvent<HTMLDivElement>) {
    if (!sesionId || !activa || e.metaKey || e.ctrlKey || e.altKey) return;
    if (TECLAS.has(e.key)) {
      e.preventDefault();
      orden(sesionId, { orden: 'tecla', tecla: e.key });
    } else if (e.key.length === 1) {
      e.preventDefault();
      orden(sesionId, { orden: 'texto', texto: e.key });
    }
  }

  const pasos = [t('cuentasClaude.reconexion.paso1', { nombre }), t('cuentasClaude.reconexion.paso2'), t('cuentasClaude.reconexion.paso3')];
  const paso = PASO[fase];

  return (
    <Dialogo.Root open onOpenChange={(abierta) => !abierta && cerrar()}>
      <Dialogo.Portal>
        <Dialogo.Overlay className="fixed inset-0 z-50 bg-black/80 animate-in fade-in-0" />
        <Dialogo.Content
          className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-oficina-panel p-4 text-oficina-texto outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[92dvh] sm:w-[1080px] sm:max-w-[96vw] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-o-m sm:border sm:border-oficina-linea sm:p-5"
          data-testid="ventana-reconexion"
        >
          <div className="flex items-center justify-between">
            <Dialogo.Title className="text-o-titulo3 font-semibold">{t('cuentasClaude.reconexion.titulo', { nombre })}</Dialogo.Title>
            <Dialogo.Close asChild>
              <button type="button" aria-label={t('cuentasClaude.cerrar')} className="inline-flex h-11 w-11 items-center justify-center rounded-o-s text-oficina-texto3 hover:bg-oficina-hover hover:text-oficina-texto focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
                <X className="h-5 w-5" aria-hidden />
              </button>
            </Dialogo.Close>
          </div>
          <Dialogo.Description className="sr-only">{t('cuentasClaude.reconexion.explica')}</Dialogo.Description>

          <ol className="mt-3 grid gap-2 sm:grid-cols-3">
            {pasos.map((p, i) => (
              <li
                key={p}
                className={cn(
                  'rounded-o-s border bg-oficina-elevada px-3 py-2 text-o-sub',
                  i + 1 === paso ? 'border-oficina-acento text-oficina-texto' : i + 1 < paso ? 'border-oficina-linea text-oficina-texto2' : 'border-oficina-linea text-oficina-texto3',
                )}
              >
                <b className={cn('mr-1.5', i + 1 === paso ? 'text-oficina-acento' : 'text-oficina-texto3')}>{i + 1}</b>
                {p}
              </li>
            ))}
          </ol>
          <p className="mt-2.5 text-o-mini leading-relaxed text-oficina-texto3">{t('cuentasClaude.reconexion.explica')}</p>

          {fase === 'conectada' ? (
            <Resultado ok texto={t('cuentasClaude.reconexion.conectada', { nombre })} alCerrar={alCerrar} />
          ) : fase === 'fallo' || fase === 'cancelada' ? (
            <Resultado
              texto={fase === 'cancelada' ? t('cuentasClaude.reconexion.cancelada') : (motivo ?? t('cuentasClaude.reconexion.noSePudo'))}
              alCerrar={alCerrar}
              alReintentar={() => {
                sesionRef.current = null;
                setSesionId(null);
                setIntento((n) => n + 1);
              }}
            />
          ) : (
            <>
              <div
                ref={pantalla}
                tabIndex={0}
                onKeyDown={alTecla}
                onPaste={(e) => {
                  const v = e.clipboardData.getData('text');
                  if (v && sesionId && activa) {
                    e.preventDefault();
                    orden(sesionId, { orden: 'texto', texto: v.slice(0, 500) });
                  }
                }}
                onWheel={(e) => sesionId && activa && orden(sesionId, { orden: 'rueda', dy: Math.max(-2000, Math.min(2000, Math.round(e.deltaY))) })}
                className="relative mt-3 overflow-hidden rounded-o-s border border-oficina-linea bg-black outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
                data-testid="pantalla-reconexion"
              >
                <div className="flex h-7 items-center gap-2 bg-oficina-elevada px-3 text-o-mini text-oficina-texto3">{t('cuentasClaude.reconexion.barra')}</div>
                {cuadro ? (
                  <img src={cuadro} alt={t('cuentasClaude.reconexion.pantalla')} onClick={alClic} className="block aspect-[1000/720] w-full cursor-pointer select-none" draggable={false} />
                ) : (
                  <div className="flex aspect-[1000/720] w-full items-center justify-center gap-2 text-o-texto text-oficina-texto3">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {fase === 'comprobando_ip' || fase === 'pidiendo' ? t('cuentasClaude.reconexion.comprobandoIp') : t('cuentasClaude.reconexion.abriendo')}
                  </div>
                )}
                {fase === 'comprobando' ? (
                  <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 text-o-texto">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t('cuentasClaude.reconexion.comprobando')}
                  </div>
                ) : null}
              </div>
              {aviso ? <p className="mt-2 text-o-mini text-oficina-bloqueado">{aviso}</p> : null}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (sesionId && texto) orden(sesionId, { orden: 'texto', texto });
                    setTexto('');
                  }}
                >
                  <input
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder={t('cuentasClaude.reconexion.escribir')}
                    className="h-11 min-w-0 flex-1 rounded-o-s border border-oficina-linea bg-oficina-fondo px-3 text-o-texto text-oficina-texto placeholder:text-oficina-texto3 focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
                  />
                  <button type="submit" disabled={!activa} className="h-11 rounded-o-s bg-oficina-elevada px-4 text-o-texto font-semibold text-oficina-texto ring-1 ring-oficina-linea hover:bg-oficina-hover disabled:opacity-50">
                    {t('cuentasClaude.reconexion.enviar')}
                  </button>
                </form>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (sesionId && enlace) orden(sesionId, { orden: 'enlace', url: enlace.trim() });
                    setEnlace('');
                  }}
                >
                  <input
                    value={enlace}
                    onChange={(e) => setEnlace(e.target.value)}
                    placeholder={t('cuentasClaude.reconexion.pegarEnlace')}
                    className="h-11 min-w-0 flex-1 rounded-o-s border border-oficina-linea bg-oficina-fondo px-3 text-o-texto text-oficina-texto placeholder:text-oficina-texto3 focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
                  />
                  <button type="submit" disabled={!activa} className="h-11 rounded-o-s bg-oficina-acento px-4 text-o-texto font-semibold text-oficina-acento-tinta hover:bg-oficina-acento-hover disabled:opacity-50">
                    {t('cuentasClaude.reconexion.abrirEnlace')}
                  </button>
                </form>
              </div>
              <p className="mt-2 text-o-mini font-medium text-oficina-bloqueado">{t('cuentasClaude.reconexion.noEnTuOrdenador')}</p>
            </>
          )}
        </Dialogo.Content>
      </Dialogo.Portal>
    </Dialogo.Root>
  );
}

function Resultado({ ok = false, texto, alCerrar, alReintentar }: { ok?: boolean; texto: string; alCerrar: () => void; alReintentar?: () => void }) {
  return (
    <div className="mt-6 flex flex-col items-center gap-4 py-8 text-center">
      {ok ? <CheckCircle2 className="h-10 w-10 text-oficina-trabajando" aria-hidden /> : null}
      <p className={cn('max-w-lg text-o-cuerpo leading-relaxed', ok ? 'text-oficina-texto' : 'text-oficina-texto2')}>{texto}</p>
      <div className="flex gap-2">
        {alReintentar ? (
          <button type="button" onClick={alReintentar} className="h-11 rounded-o-s bg-oficina-acento px-4 text-o-texto font-semibold text-oficina-acento-tinta hover:bg-oficina-acento-hover">
            {t('cuentasClaude.reconexion.reintentar')}
          </button>
        ) : null}
        <button type="button" onClick={alCerrar} className="h-11 rounded-o-s bg-oficina-elevada px-4 text-o-texto font-semibold text-oficina-texto ring-1 ring-oficina-linea hover:bg-oficina-hover">
          {t('cuentasClaude.cerrar')}
        </button>
      </div>
    </div>
  );
}
